import { useRouter } from 'next/router';
import React, { useCallback, useEffect, useState } from 'react';
import { useQuery } from 'urql';

import { useLocation } from 'hooks/useLocation';
import { usePreferences } from 'hooks/usePreferences';
import { useTranslate } from 'hooks/useTranslations';
import { useSiteConfig } from 'hooks/useSiteConfig';
import { isServer } from 'utils/constants';
import { Product } from '__generated__/graphql';
import { Link } from 'ui/elements/Link';
import { buildProductUrl } from 'utils/buildProductUrl';
import { PRODUCT_QUERY } from 'gql/queries/product';
import { resizeProductImage } from 'utils/image';
import { useSimpleCart } from 'hooks/useSimpleCart';

import { Img } from './Img';
import CloseButton from './CloseButton';
export const PickUpWhereYouLeftOff: React.FC = () => {
  const t = useTranslate();
  const router = useRouter();
  const { countryCode } = useSiteConfig();
  const { location } = useLocation();
  const { siteCountry, userCountry } = location || {};
  const { preferences, setPreferences } = usePreferences();
  const { cart } = useSimpleCart();

  const lastViewedProduct = preferences['last-viewed-product'];

  // Don't show on the same product
  const differentProduct = router.query.id !== lastViewedProduct?.id;

  // Only show if lastProduct was in the same country as the site
  const sameCountry = lastViewedProduct?.country === countryCode;

  // Only show if user has preferred the site region, or is already viewing their regional site
  const locationPopupHidden =
    preferences['prefers-region'] || userCountry?.id === siteCountry?.id;

  const isInSameSessionAsSettingProduct =
    window.sessionStorage.getItem('in-session') === 'true';

  // Check if we're on cart or checkout pages
  const isCartOrCheckoutPage =
    router.asPath.includes('/cart') || router.asPath.includes('/checkout');

  // Check if cart is empty
  const isCartEmpty = !cart?.productCount;

  // Only hide the popup on cart/checkout pages when cart has items
  const shouldHideOnCurrentPage = isCartOrCheckoutPage && !isCartEmpty;

  const initialShouldShow =
    differentProduct &&
    sameCountry &&
    locationPopupHidden &&
    !isInSameSessionAsSettingProduct &&
    !shouldHideOnCurrentPage;

  const [shouldShow, setShouldShow] = useState(initialShouldShow);

  const [query] = useQuery<{ product: Product }>({
    query: PRODUCT_QUERY,
    variables: { id: lastViewedProduct?.id },
    pause: isServer || !lastViewedProduct || !initialShouldShow,
  });

  const clearLastViewedProduct = useCallback(() => {
    setPreferences(curr => ({
      ...curr,
      'last-viewed-product': undefined,
    }));
  }, [setPreferences]);

  const closePopup = useCallback(() => {
    setShouldShow(false);
    clearLastViewedProduct();
  }, [clearLastViewedProduct]);

  let product;
  if (lastViewedProduct && query.data?.product) {
    const productData = query.data?.product;

    if (productData?.orderable || productData?.displayOutOfStock) {
      const chosenVariant = productData?.variations.find(
        x => x.colorValue === lastViewedProduct?.swatch
      );

      product = {
        name: productData.name,
        color: chosenVariant?.colorName,
        image: chosenVariant?.images[0],
        url: buildProductUrl(lastViewedProduct?.id, productData?.name, {
          color: lastViewedProduct?.swatch,
          ...(lastViewedProduct?.size && { size: lastViewedProduct?.size }),
        }),
      };
    }
  }

  // This ensures shouldShow stays in sync with the calculated conditions
  // This fixes an issue where the popup would sometimes appear on cart/checkout pages even when initialShouldShow was false
  useEffect(() => {
    setShouldShow(initialShouldShow);
  }, [initialShouldShow]);

  // cleanup preferences if the query fails
  useEffect(() => {
    if (query.error) {
      clearLastViewedProduct();
    }
  }, [query.error, clearLastViewedProduct]);

  // close popup after N seconds
  useEffect(() => {
    if (product) {
      const timeout = setTimeout(() => {
        if (product) closePopup();
      }, 10000);

      return () => clearTimeout(timeout);
    }
  }, [product, closePopup]);

  // render nothing unless we have a product to show
  if (!(product && shouldShow)) {
    return null;
  }

  return (
    <div className="fixed right-0 bottom-0 z-[80] p-2 sm:p-5">
      <div
        className="bg-white shadow-2xl p-5 space-y-3 border rounded-lg font-display max-w-sm"
        data-test-id="pickup-where-you-left-off"
      >
        <div className="flex items-center">
          <h1 className="font-bold uppercase">{t('pickUpWhereYouLeftOff')}</h1>
          <CloseButton
            className="absolute right-0 mr-7 sm:mr-10 opacity-60"
            onClick={closePopup}
          />
        </div>
        <div className="border p-4 space-y-2">
          <div className="grid grid-cols-10 gap-4 xs:gap-4">
            <div className="w-full self-center flex-none col-span-4">
              <Img
                src={resizeProductImage(product.image?.href, 250)}
                width={250}
                alt={product.image?.alt || product.name}
              />
            </div>
            <div className="m-auto col-span-6 space-y-2">
              <h2 className="font-bold text-puma-black leading-tight -mt-4">
                {product.name}
              </h2>
              {product.color && (
                <div className="text-xs xs:text-sm" data-test-id="color">
                  {t('pickUpWhereYouLeftOffColorPre') && (
                    <span>{t('pickUpWhereYouLeftOffColorPre')}</span>
                  )}{' '}
                  {product.color}
                </div>
              )}
            </div>
          </div>
        </div>
        <Link
          as="button"
          variant="primary"
          className="w-full"
          onClick={closePopup}
          data-test-id="view-product"
          href={product.url}
        >
          {t('viewProduct')}
        </Link>
      </div>
    </div>
  );
};
